<script setup lang="ts">
import { ref, onMounted } from 'vue'
import HomeSlideshow from '../../home/HomeSlideshow.vue'
import AuthModal from '../../auth/AuthModal.vue'
import { api } from '../../../api/Api'
import { useRoute } from 'vue-router'
import { useNotificationStore } from '../../../stores/notification'
import { tokenManager } from '../../../api/utils/tokenManager'
import { email, required } from '../../../utils/validation'
import TextInput from '../../ui/form/TextInput.vue'
import { useFormValidation } from '../../../composables/useFormValidation'
const notificationStore = useNotificationStore()

const showLoginModal = ref(false)
const isComplete = ref(false)

const { fields, formData, setFieldRules, validateForm } =
  useFormValidation({
    email: '',
    password: '',
    confirm_password: '',
    cid: '',
    uid: '',
  });

const confirmPasswordRule = (message = 'Passwords do not match') => ({
  validate: (value: string) => value === formData.value.password,
  message,
})

setFieldRules('email', [required('Email is required'), email()])
setFieldRules('password', [required('Password is required')])
setFieldRules('confirm_password', [
required('Confirmation Password is required'),
    confirmPasswordRule(),
])

const isLoading = ref(false)

const error = ref('')

const route = useRoute()
onMounted(async () => {
    tokenManager.logout()
    if (route.params.id == '') {
        error.value = 'Invalid Link or Activation Code'
        return;
    }
    const param = route.params.id as string;
    const params = param.split("+")
    const apiData = {
        code: params[0],
        company: params[1]
    }
    fields.value.cid.value = params[1]
    fields.value.uid.value = params[0]
    const response = await api.post('activate-user-email', apiData,  {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      skipLoading: true
    })
    .catch(err => {
      error.value = err.response?.data?.errors || 'An error occurred'
    })
    if (response && response.success == true) {
        notificationStore.addNotification({
            title: 'Activation Successful!',
            message: 'Thank you for choosing FaseyhaPOS, your email is verified, please create a password of your choice.',
            type: 'info',
            action: {
                label: 'Login',
                onClick: () => {
                    showLoginModal.value = true
                }
            }
        })
        fields.value.email.value = response.data.email
        isComplete.value = true
        //tokenManager.setMessage('user_email_activated')
        //window.location.href = '/'
    } else if (response) {
        error.value = response.errors
    }
    
})

const handleSubmit = async () => {
  if (validateForm()) {
    const response = await api.post('update-user-password', formData.value);
    if (response.success == true) {        
        tokenManager.setMessage('user_email_activated')
        window.location.href = '/'
    } else {
        error.value = 'could not udpate password'
    }
  }
};

</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
    <div class="max-w-4xl mx-auto">
      <!-- Progress Section -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
          Activate Email
        </h1>

        <!-- Error Alert -->
        <div 
            v-if="error"
            class="p-4 flex rounded-md bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800"
        >
            <p class="text-sm  py-2 px-4 text-center text-red-800 dark:text-red-200">{{ error }}</p>
            <button
                @click="showLoginModal = true"
                class="mt-10 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
                If you already have activated you emaill, try Log In to Continue
            </button>
        </div>

        <div id="" class="grid md:grid-cols-3 gap-6">
            <div></div>
            <div>
                <form v-if="isComplete" @submit.prevent="handleSubmit" class="mt-8 space-y-6" novalidate>
                    <TextInput
                        v-model="fields.email.value"
                        label="Email"
                        :readonly="true"
                        :error="
                            fields.email.touched ? fields.email.error : null
                        "
                        @blur="fields.email.touched = true"
                        />

                    <TextInput
                        v-model="fields.password.value"
                        type="password"
                        label="Password"
                        :error="
                            fields.password.touched ? fields.password.error : null
                        "
                        @blur="fields.password.touched = true"
                        />

                    <TextInput
                        v-model="fields.confirm_password.value"
                        type="password"
                        label="Cofirm Password"
                        :error="
                            fields.confirm_password.touched ? fields.confirm_password.error : null
                        "
                        @blur="fields.confirm_password.touched = true"
                        />

                    <div class="flex flex-col space-y-3">
                        <button
                        type="submit"
                        :disabled="isLoading"
                        class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                        <span v-if="isLoading">Sending...</span>
                        <span v-else>Update and Proceed to Login</span>
                        </button>

                    </div>
                </form>
            </div>
            <div></div>
        </div>
      </div>

      <!-- Slideshow Section -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <HomeSlideshow />
      </div>
    </div>

    <!-- Login Modal -->
    <AuthModal
      v-if="showLoginModal"
      @close="showLoginModal = false"
    />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>